import { Link } from "react-router-dom";
import { Calendar, MapPin, Phone } from "lucide-react";

const MONTHS = ['januari','februari','mars','april','maj','juni','juli','augusti','september','oktober','november','december'];

function formatDate(dateStr) {
  if (!dateStr) return "";
  const d = new Date(dateStr);
  return `${d.getDate()} ${MONTHS[d.getMonth()]}`;
}

const TAG_COLORS = {
  "Hjälp i vardagen": "bg-pine/10 text-pine",
  "Hand mot hand": "bg-falu/10 text-falu",
  "Köp & sälj lokalt": "bg-amber-50 text-amber-800",
  "Evenemang": "bg-bone-alt text-pine",
};

export default function ContentCard({ item }) {
  const isEvent = item.typ === "evenemang";
  const tag = isEvent ? "Evenemang" : item.rubrik;
  const tagColor = TAG_COLORS[tag] || "bg-bone-alt text-bark";

  const metaParts = [];
  if (isEvent) {
    if (item.datum) metaParts.push(formatDate(item.datum));
    if (item.tid) metaParts.push(item.tid);
    if (item.plats) metaParts.push(item.plats);
  } else {
    if (item.publiceringsdatum) metaParts.push(formatDate(item.publiceringsdatum));
    if (item.ort) metaParts.push(item.ort);
    if (item.rubrik === "Köp & sälj lokalt") {
      if (item.pristyp === "belopp" && item.prisbelopp) metaParts.push(item.prisbelopp);
      else if (item.pristyp) metaParts.push(item.pristyp);
    }
  }

  const phone = !isEvent ? (item.kontakt_telefon || null) : (item.kontaktvag || null);
  const linkTo = isEvent ? `/evenemang/${item.id}` : `/anslag/${item.id}`;
  const isExempel = item.exempel === true;

  return (
    <div
      data-testid={`content-card-${item.id}`}
      className="bg-parchment border border-warm-border rounded-sm p-5 sm:p-6 transition-all hover:border-bark/30 group"
    >
      <Link to={linkTo} className="block no-underline">
        <div className="flex items-center gap-2 mb-3">
          <span className={`inline-block text-xs font-body font-semibold uppercase tracking-wide px-2.5 py-1 rounded-sm ${tagColor}`}>
            {tag}
          </span>
          {isExempel && (
            <span className="inline-block text-xs font-body font-medium px-2 py-0.5 rounded-sm bg-amber-50 text-amber-700">
              Exempelanslag
            </span>
          )}
        </div>
        {(() => {
          const media = Array.isArray(item.galleri) && item.galleri.length > 0
            ? item.galleri
            : (item.bild_url ? [item.bild_url] : []);
          if (media.length === 0) return null;
          const first = media[0];
          const full = first.startsWith("http") ? first : `${process.env.REACT_APP_BACKEND_URL}${first}`;
          const isVideo = /\.(mp4|mov|webm|m4v)$/i.test(first);
          return (
            <div data-testid={`content-card-media-${item.id}`} className="relative mb-3 -mx-1">
              {isVideo ? (
                <div className="relative w-full max-w-[420px] aspect-video rounded-sm overflow-hidden border border-warm-border bg-ink">
                  <video src={`${full}#t=0.5`} muted playsInline preload="metadata" className="w-full h-full object-cover" />
                  <span className="absolute bottom-1 left-1 text-[10px] font-bold uppercase tracking-wider px-1.5 py-0.5 rounded-sm bg-parchment/90 text-bark pointer-events-none">Video</span>
                </div>
              ) : (
                <img
                  src={full}
                  alt={item.titel || ""}
                  loading="lazy"
                  className="w-full max-w-[420px] max-h-56 object-cover rounded-sm border border-warm-border"
                />
              )}
              {media.length > 1 && (
                <span className="absolute top-1 right-1 text-[10px] font-bold uppercase tracking-wider px-1.5 py-0.5 rounded-sm bg-parchment/90 text-bark pointer-events-none">
                  +{media.length - 1}
                </span>
              )}
            </div>
          );
        })()}
        <h3 className="font-heading text-xl sm:text-2xl font-semibold text-ink mb-2 group-hover:text-falu transition-colors">
          {item.titel}
        </h3>
        {item.beskrivning && (
          <p className="text-sm text-bark line-clamp-2 mb-3 leading-relaxed">{item.beskrivning}</p>
        )}
        <div className="flex flex-wrap items-center gap-x-3 gap-y-1 text-xs text-bark/70">
          {isEvent && item.datum && (
            <span className="flex items-center gap-1"><Calendar size={13} /> {formatDate(item.datum)}{item.tid ? `, ${item.tid}` : ""}</span>
          )}
          {isEvent && item.plats && (
            <span className="flex items-center gap-1"><MapPin size={13} /> {item.plats}</span>
          )}
          {!isEvent && <span>{metaParts.join(" \u00B7 ")}</span>}
        </div>
      </Link>
      {phone && (
        <a
          href={`tel:${phone.replace(/\s/g, "")}`}
          data-testid={`card-phone-${item.id}`}
          onClick={(e) => e.stopPropagation()}
          className="inline-flex items-center gap-2 mt-3 pt-3 border-t border-warm-border text-sm font-semibold text-pine hover:text-pine-hover no-underline transition-colors"
        >
          <Phone size={15} /> {phone}
        </a>
      )}
    </div>
  );
}
